/**
 * video_render - one-shot poll adapter for managed video renders (ENG-7658).
 *
 * When generate_video submits a render to xAI, the API auto-registers one of
 * these triggers (zero agent-side setup) so the agent does not have to poll:
 * the TriggerPollExecutor cron watches GET /v1/videos/{request_id} and, when
 * the render leaves 'pending', delivers ONE direct-chat message telling the
 * agent to collect the result via generate_video({ request_id: <resume
 * handle> }). The adapter then signals `complete: true` and the executor
 * revokes the trigger (the ENG-7658 one-shot seam) - unlike gdrive-comments,
 * this is not a standing subscription.
 *
 * The adapter never talks HTTP itself: the executor injects a
 * VideoRenderStatusFetcher via ctx.credentials (the gdrive-comments pattern),
 * which is what carries the platform XAI key. Events are sourceTrust
 * 'internal' - every string in the message is platform-authored (the resume
 * handle and request id are platform-generated; provider error detail is
 * reduced to a sanitized code) - so delivery renders the clean un-fenced
 * header.
 *
 * Delivery is exactly-once regardless of poll retries: dedupKey is derived
 * from the immutable request_id and enforced by UNIQUE(trigger_id, dedup_key)
 * on trigger_events.
 */

import type { TriggerEvent, TriggerPollContext, TriggerPollResult, TriggerSourceAdapter, TriggerSubscription } from '../types.js';
import { registerTriggerSource } from '../registry.js';

export const VIDEO_RENDER_TRIGGER_PROVIDER = 'video_render';

/** Config stored on the trigger row at submit time (free jsonb). */
export interface VideoRenderTriggerConfig {
  /** xAI's render id - the identity the status poll checks. */
  request_id: string;
  /**
   * The SIGNED resume handle (HMAC-bound to the agent) the agent passes back
   * to generate_video to collect. Platform-generated; safe to embed verbatim
   * in the internal-trust message.
   */
  resume_handle: string;
}

/** The render states the fetcher reports (a reduction of xAI's status shape). */
export type VideoRenderState =
  | { state: 'pending' }
  | { state: 'done' }
  | { state: 'failed'; code?: string }
  | { state: 'expired' }
  /** The provider no longer knows the id (404/410) - terminal. */
  | { state: 'not_found' };

/**
 * Executor-injected client (via ctx.credentials). Kept as a one-method
 * interface so tests and the executor's concrete xAI implementation stay
 * interchangeable; the adapter never sees the API key.
 */
export interface VideoRenderStatusFetcher {
  fetchRenderState(requestId: string): Promise<VideoRenderState>;
}

function isFetcher(v: unknown): v is VideoRenderStatusFetcher {
  return !!v && typeof (v as VideoRenderStatusFetcher).fetchRenderState === 'function';
}

/** Only a provider-shaped error code ever reaches the message (never free text). */
function sanitizeCode(code: string | undefined): string | null {
  if (!code) return null;
  const cleaned = code.trim().toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 64);
  return cleaned || null;
}

function collectInstruction(config: VideoRenderTriggerConfig): string {
  return (
    `Collect it now: call generate_video with ONLY {"request_id": "${config.resume_handle}"} ` +
    `(do not resend the prompt - resubmitting starts a new billed render). The tool returns a ` +
    `short-lived download URL; download it to a file under your project directory and deliver ` +
    `it with your channel's upload_file tool.`
  );
}

async function poll(ctx: TriggerPollContext, trigger: TriggerSubscription): Promise<TriggerPollResult> {
  const config = trigger.config as Partial<VideoRenderTriggerConfig>;
  if (!config.request_id || !config.resume_handle) {
    throw new Error('video_render trigger config requires request_id and resume_handle');
  }
  if (!isFetcher(ctx.credentials)) {
    throw new Error('video_render poll requires a VideoRenderStatusFetcher via ctx.credentials');
  }

  const state = await ctx.credentials.fetchRenderState(config.request_id);
  if (state.state === 'pending') {
    return { events: [], cursor: ctx.cursor };
  }

  const occurredAt = new Date().toISOString();
  const base = {
    provider: VIDEO_RENDER_TRIGGER_PROVIDER,
    occurredAt,
    // One event per render, ever - immutable identity, dedup-enforced.
    dedupKey: `vr:${config.request_id}`,
    // Platform-originated content only (see module header) - renders the clean
    // un-fenced internal header instead of the external-content fence.
    sourceTrust: 'internal' as const,
    meaningful: true,
  };

  let event: TriggerEvent;
  if (state.state === 'done') {
    event = {
      ...base,
      title: 'Video render complete',
      body: `Your video render has finished. ${collectInstruction(config as VideoRenderTriggerConfig)}`,
    };
  } else if (state.state === 'failed') {
    const code = sanitizeCode(state.code);
    event = {
      ...base,
      title: 'Video render failed',
      body:
        `Your video render failed provider-side${code ? ` (code: ${code})` : ''}. ` +
        `It was not delivered and cannot be collected. If the video is still needed, submit a new ` +
        `generate_video request (a fresh prompt - the old request_id is dead).`,
    };
  } else {
    // 'expired' | 'not_found': the render output is gone provider-side.
    event = {
      ...base,
      title: 'Video render expired',
      body:
        `Your video render expired before it was collected (the provider no longer holds the ` +
        `output). If the video is still needed, submit a new generate_video request (a fresh ` +
        `prompt - the old request_id is dead).`,
    };
  }

  // Terminal either way: deliver the one event, then ask the executor to
  // revoke this trigger (one-shot).
  return { events: [event], cursor: ctx.cursor, complete: true };
}

export const videoRenderTriggerAdapter: TriggerSourceAdapter = {
  provider: VIDEO_RENDER_TRIGGER_PROVIDER,
  kind: 'poll',
  poll,
};

registerTriggerSource(videoRenderTriggerAdapter);
